import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShareLinkFile } from "../entities";
import { BlurHashCanvas } from "./blurHashCanvas";
import { cssVar } from "../utils/cssVar";
import { basenameFromPath, formatFileSize } from "../utils/fileFormat";
import { FileIcon } from "../utils/fileIcon";
import { selectableProps } from "../utils/selectable";
import { SelectionCheckbox } from "./selectionCheckbox";

/**
 * Surface tint for a highlighted (keyboard-focused or deep-linked) file card /
 * list row. Defined in `index.css` (`--file-focus-tint`) so the tint, the
 * deep-link blink keyframes and this constant all stay in sync.
 */
export const KEYBOARD_FOCUS_TINT = "var(--file-focus-tint)";

/**
 * Drives the deep-link "attention blink": when an item is highlighted from a
 * reference link (`active`), returns `true` shortly after mount — once the page
 * has revealed and scrolled the item into view — so the caller applies the blink
 * animation class. Returns `false` otherwise (and for keyboard focus).
 */
export function useDeepLinkBlink(active: boolean): boolean {
  const [blink, setBlink] = useState(false);
  useEffect(() => {
    if (!active) {
      setBlink(false);
      return;
    }
    const id = window.setTimeout(() => setBlink(true), 550);
    return () => window.clearTimeout(id);
  }, [active]);
  return blink;
}

/**
 * Tactile "push" feedback for a pressable card/row: tracks whether a pointer
 * (mouse or touch — pointer events unify both) is currently held down, so the
 * element can scale down while pressed and spring back on release or when the
 * pointer leaves. Returns the flag plus handlers to spread onto the element.
 */
export function usePressEffect() {
  const [isPressed, setIsPressed] = useState(false);
  const press = {
    onPointerDown: () => setIsPressed(true),
    onPointerUp: () => setIsPressed(false),
    onPointerLeave: () => setIsPressed(false),
    onPointerCancel: () => setIsPressed(false),
  };
  return { isPressed, press };
}

/** Press duration (ms) that turns a touch into a selection long-press. */
const LONG_PRESS_MS = 450;
/** Finger travel (px) that cancels a pending long-press (treated as a scroll). */
const LONG_PRESS_MOVE_CANCEL_PX = 10;

/**
 * Turns a still touch press into `onLongPress` (the touch gesture to enter
 * selection mode). The browser synthesizes a click right after, so the click
 * handler must call `consumeClick()` first and bail when it returns true.
 * Spread `handlers` onto the pressable element.
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<number | undefined>(undefined);
  const fired = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  // Latest callback without churning the memoized handlers.
  const callbackRef = useRef(onLongPress);
  callbackRef.current = onLongPress;

  const cancel = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  useEffect(() => cancel, [cancel]);

  const handlers = useMemo(
    () => ({
      onTouchStart: (e: React.TouchEvent) => {
        fired.current = false;
        const touch = e.touches[0];
        start.current = { x: touch.clientX, y: touch.clientY };
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          fired.current = true;
          callbackRef.current();
        }, LONG_PRESS_MS);
      },
      onTouchMove: (e: React.TouchEvent) => {
        const s = start.current;
        if (!s) return;
        const touch = e.touches[0];
        if (
          Math.abs(touch.clientX - s.x) > LONG_PRESS_MOVE_CANCEL_PX ||
          Math.abs(touch.clientY - s.y) > LONG_PRESS_MOVE_CANCEL_PX
        ) {
          cancel();
        }
      },
      onTouchEnd: cancel,
      onTouchCancel: cancel,
    }),
    [cancel]
  );

  const consumeClick = useCallback(() => {
    if (!fired.current) return false;
    fired.current = false;
    return true;
  }, []);

  return { handlers, consumeClick };
}

/**
 * Whether the backend is expected to have a thumbnail for this file, so we can
 * skip the request for files that have none.
 *
 * The BlurHash and the thumbnails come from the same post-processing step, which
 * the spec says produces neither when no previewer can extract an image — so the
 * BlurHash's presence is our signal that a thumbnail exists
 * (https://dev.skalio.net/drive/internal/postprocessing/).
 *
 * Gate for the thumbnail endpoint only: a file that returns false still opens in
 * the preview gallery, which uses the broader on-demand preview endpoint.
 */
export function fileMayHaveThumbnail(file: ShareLinkFile): boolean {
  return file.blurHash != null;
}

/**
 * How activation maps to clicks:
 *  - `"preview"` (default: share-link, and touch in the published space): a
 *    plain click/tap opens the preview, `Shift`+click toggles selection.
 *  - `"select"` (published space), desktop-file-manager conventions: a plain
 *    click moves the selection to the clicked file, cmd/ctrl+click toggles it
 *    in/out, shift+click selects the range from the last-clicked file, and a
 *    double click opens the preview (its clicks simply select the file first,
 *    so selection reacts instantly).
 */
export type ItemActivation = "preview" | "select";

/**
 * Shared interaction logic for a single shared-file item, whether rendered as a
 * grid {@link FileCard} or a list row.
 *
 * Derives the display name and wires the activation behavior; see
 * {@link ItemActivation} for how clicks and keys map in each mode.
 *
 * The preview itself is owned by the parent section (one shared overlay), not by
 * the item — so it survives a grid↔list layout switch that would otherwise
 * unmount this item and lose a locally-held "open" flag.
 */
export function useFileItemInteraction(
  file: ShareLinkFile,
  onToggleSelect: (uid: string) => void,
  onOpenPreview: (file: ShareLinkFile) => void,
  activation: ItemActivation = "preview",
  /** Plain click in `"select"` mode: select *only* this file (falls back to toggle). */
  onSelectExclusive?: (uid: string) => void,
  /** Shift+click in `"select"` mode: range from the last-clicked file (falls back to toggle). */
  onSelectRange?: (uid: string) => void
) {
  const displayName = basenameFromPath(file.path) || file.uid;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (activation === "select") {
        // Cmd/Ctrl + click toggles the file in/out of the selection.
        if (e.metaKey || e.ctrlKey) {
          onToggleSelect(file.uid);
          return;
        }
        // Shift + click selects the contiguous range from the anchor.
        if (e.shiftKey) {
          (onSelectRange ?? onToggleSelect)(file.uid);
          return;
        }
        // A plain click selects just this file, immediately.
        (onSelectExclusive ?? onToggleSelect)(file.uid);
        return;
      }
      if (e.shiftKey) {
        onToggleSelect(file.uid);
        return;
      }
      onOpenPreview(file);
    },
    [
      activation,
      file,
      onToggleSelect,
      onOpenPreview,
      onSelectExclusive,
      onSelectRange,
    ]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (activation !== "select") return;
      // A modified double click has no behaviour of its own, so it is left to
      // the two clicks `handleClick` already saw: shift keeps growing the range
      // and cmd keeps toggling, instead of collapsing the selection onto this
      // one file and opening it.
      if (e.shiftKey || e.metaKey || e.ctrlKey) return;
      // Pin the selection to the opened file (a touch tap may have toggled it off).
      onSelectExclusive?.(file.uid);
      onOpenPreview(file);
    },
    [activation, file, onOpenPreview, onSelectExclusive]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Only the card/row root itself activates. Bail for keys bubbling up from a
      // descendant tab stop (e.g. the selection checkbox).
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter") {
        e.preventDefault();
        onOpenPreview(file);
      } else if (e.key === " ") {
        e.preventDefault();
        // Space toggles selection in "select" mode, otherwise opens the preview.
        if (activation === "select") onToggleSelect(file.uid);
        else onOpenPreview(file);
      }
    },
    [activation, file, onOpenPreview, onToggleSelect]
  );

  return { displayName, handleClick, handleDoubleClick, handleKeyDown };
}

/**
 * Visual properties of {@link FileCard} surface chrome.
 *
 * Every color is optional — defaults match the public share-link viewer's
 * light theme. Override individual values to embed the card on a darker
 * surface, theme it per-tenant, or align it with a host container.
 */
export type FileCardAppearance = {
  /** Background color in the idle state. Defaults to `"#F7F7F7"`. */
  backgroundColor?: string;
  /** Background color when selected. Defaults to the CSS variable `--primary-light-color`. */
  selectedBackgroundColor?: string;
  /** Border color when selected. Defaults to the CSS variable `--primary-color`. */
  selectedBorderColor?: string;
  /** CSS `aspect-ratio` value for the card. Defaults to `"174 / 204"`. */
  aspectRatio?: string;
  /** CSS `border-radius` value. Defaults to `"6px"`. */
  borderRadius?: string;
};

/**
 * How to source a card's thumbnail. The caller owns the endpoint, auth and any
 * cache-busting; the card just loads whatever URL it returns into a native
 * `<img>`.
 */
export type ThumbnailSource = {
  /** Builds the thumbnail URL for a file. */
  buildUrl: (file: ShareLinkFile) => string;
};

export type FileCardProps = {
  /** The shared file to render. */
  file: ShareLinkFile;
  /** Whether the card is currently part of the user's selection. */
  isSelected: boolean;
  /** Invoked with the file's UID when the user toggles the selection checkbox. */
  onToggleSelect: (uid: string) => void;
  /** Invoked with the file when the user activates the card to open its preview. */
  onOpenPreview: (file: ShareLinkFile) => void;
  /**
   * How to source this card's thumbnail. Omit to show the file-type icon
   * instead of a thumbnail.
   */
  thumbnails?: ThumbnailSource;
  /**
   * True while a marquee drag-selection is in progress. Suppresses the hover
   * animation and keeps the cursor an arrow (rather than a pointer) so dragging
   * a selection box never looks like card hovering.
   */
  selecting?: boolean;
  /**
   * Where the selection checkbox sits:
   *  - `"nameBar"` (default): inline beside the name/size, always visible and in
   *    the tab order — the share-link viewer's behavior.
   *  - `"overlay"`: pinned over the thumbnail's top-right corner, revealed only
   *    on hover / when selected, and skipped by Tab.
   */
  checkboxPlacement?: "nameBar" | "overlay";
  /**
   * For the `"overlay"` checkbox: keep it visible instead of revealing it on
   * hover / selection. Set on touch devices, which have no hover to surface it.
   */
  checkboxAlwaysVisible?: boolean;
  /** Click activation model (see {@link ItemActivation}). Defaults to `"preview"`. */
  activation?: ItemActivation;
  /** True when ≥1 file is selected; a `"preview"` tap then toggles instead of opening. */
  selectionActive?: boolean;
  /** Plain-click exclusive select for `"select"` activation (see {@link useFileItemInteraction}). */
  onSelectExclusive?: (uid: string) => void;
  /** Shift+click range select for `"select"` activation (see {@link useFileItemInteraction}). */
  onSelectRange?: (uid: string) => void;
  /**
   * Controlled "highlighted/focused" state — applies the same tint as a keyboard
   * focus. Used to spotlight a deep-linked file.
   */
  focused?: boolean;
  /**
   * When provided, a kebab (⋮) button is rendered at the name-bar's trailing
   * edge; clicking it invokes this with the button element (the caller anchors
   * its own menu) and the file. Omit for no overflow menu (share-link).
   */
  onMoreActions?: (anchorEl: HTMLElement, file: ShareLinkFile) => void;
} & FileCardAppearance;

/**
 * Grid card representing a single file inside a Drive share-link.
 *
 * The card renders:
 *  - a square thumbnail (BlurHash placeholder, lazy-loaded thumbnail image,
 *    fallback file-type icon),
 *  - the file's basename and locale-formatted size,
 *  - a selection checkbox.
 *
 * Interaction (see {@link useFileItemInteraction}):
 *  - plain click / `Enter` / `Space` opens the {@link PreviewPage} overlay,
 *  - `Shift`+click (or the checkbox) toggles selection without opening preview,
 *  - the card is keyboard-focusable (`tabIndex=0`) and shows a `:focus-visible`
 *    outline (see the `.file-card` rule in `index.css`).
 *
 * The root element carries the selectable-item marker (see {@link selectableProps})
 * so a parent can locate and box-select cards (e.g. {@link useMarqueeSelection}).
 *
 * Visual styling follows the Adobe XD specs: `#F7F7F7` surface, 6px radius and
 * a `0/3/3` drop shadow; when selected, a primary-light fill with a 2px
 * primary border (shadow retained).
 *
 * Memoized: a selection change in the parent only re-renders the cards whose
 * `isSelected` actually flips.
 */
export const FileCard = memo(function FileCard({
  file,
  isSelected,
  onToggleSelect,
  onOpenPreview,
  thumbnails,
  selecting = false,
  checkboxPlacement = "nameBar",
  checkboxAlwaysVisible = false,
  activation = "preview",
  selectionActive = false,
  onSelectExclusive,
  onSelectRange,
  focused = false,
  onMoreActions,
  backgroundColor = "#F7F7F7",
  selectedBackgroundColor,
  selectedBorderColor,
  aspectRatio = "174 / 204",
  borderRadius = "6px",
}: FileCardProps) {
  const { t, i18n } = useTranslation();
  const { displayName, handleClick, handleDoubleClick, handleKeyDown } =
    useFileItemInteraction(
      file,
      onToggleSelect,
      onOpenPreview,
      activation,
      onSelectExclusive,
      onSelectRange
    );
  const [isHovered, setIsHovered] = useState(false);
  // Deep-link blink for a referenced (focused) card.
  const blink = useDeepLinkBlink(focused);
  // Push feedback: the card scales down while pressed, springing back on release.
  const { isPressed, press } = usePressEffect();
  // Long-press selects on touch; the click synthesized after it is swallowed.
  const { handlers: pressHandlers, consumeClick } = useLongPress(() =>
    onToggleSelect(file.uid)
  );
  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      if (consumeClick()) return;
      // Touch selection mode: a tap toggles instead of opening the preview.
      if (activation === "preview" && selectionActive) {
        onToggleSelect(file.uid);
        return;
      }
      handleClick(e);
    },
    [
      consumeClick,
      activation,
      selectionActive,
      onToggleSelect,
      file.uid,
      handleClick,
    ]
  );
  // The thumbnail URL, or undefined to fall back to the file-type icon: no
  // thumbnail source was supplied, or the file has none (see fileMayHaveThumbnail,
  // which skips a request that would be a guaranteed 404). The builder owns the
  // endpoint and any cache-busting; the card just loads it.
  const thumbnailUrl =
    thumbnails && fileMayHaveThumbnail(file)
      ? thumbnails.buildUrl(file)
      : undefined;

  // No hover affordance while a marquee drag is in progress. Hover only lifts
  // the card (shadow + translate) — the surface colour never changes on hover;
  // it stays the idle fill unless the card is selected.
  const showHover = isHovered && !selecting;
  // The overlay checkbox is revealed by a hover or the selection, and stays put
  // where there is no hover to reveal it.
  const showCheckbox = isSelected || showHover || checkboxAlwaysVisible;
  const resolvedSelectedBg =
    selectedBackgroundColor ?? cssVar("--primary-light-color");
  const resolvedSelectedBorder =
    selectedBorderColor ?? cssVar("--primary-color");
  // Selected fill wins; the focus tint is a separate overlay (see below).
  const surfaceBg = isSelected ? resolvedSelectedBg : backgroundColor;

  return (
    <div
      {...selectableProps(file.uid)}
      role="button"
      tabIndex={0}
      aria-label={displayName}
      aria-pressed={isSelected}
      className="file-card relative flex select-none flex-col overflow-hidden outline-none"
      // Arms the CSS focus tint (index.css); "force" pins it for deep links.
      data-highlight={isSelected ? undefined : focused ? "force" : "focus"}
      style={{
        aspectRatio,
        borderRadius,
        cursor: selecting ? "default" : "pointer",
        backgroundColor: surfaceBg,
        // Drop shadow per fileCardSpec_1 (X 0, Y 3, Blur 3), retained when
        // selected; hover lifts the card via a translate (not scale, which
        // smears thumbnail pixels) with a larger shadow. The selection ring
        // is an *inset* shadow (drawn inside the card, not as an outer
        // wrapper) whose spread animates 0px→2px, so the border visibly grows
        // in thickness when selected and shrinks back when deselected. The
        // fill colour fades separately via the background-color transition.
        boxShadow: `${
          showHover
            ? "0px 6px 14px rgba(0,0,0,0.15)"
            : "0 3px 3px rgba(0,0,0,0.12)"
        }, inset 0 0 0 ${isSelected ? "2px" : "0px"} ${resolvedSelectedBorder}`,
        // Pressing pushes the card down (scale) and cancels the hover lift,
        // so it reads as pressing into the surface; it springs back on release.
        transform: `${
          showHover && !isPressed ? "translateY(-1px)" : "translateY(0)"
        } scale(${isPressed ? 0.97 : 1})`,
        // Selection is high-frequency, so the fill/ring snap in fast.
        transition:
          "transform 0.12s ease-out, box-shadow 0.15s ease, background-color 0.15s ease",
      }}
      {...press}
      onMouseEnter={() => {
        if (!selecting) setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      {...pressHandlers}
    >
      {/* Focus tint, painted under the content (first child); shown via the
          CSS rules keyed off data-highlight. */}
      <div
        aria-hidden
        className={`file-card-focus-overlay pointer-events-none absolute inset-0${
          blink && !isSelected ? " file-deeplink-blink-overlay" : ""
        }`}
      />
      {/* Thumbnail region — fills the space above the name bar. The thumbnail
              component owns its own padding/border so an image thumbnail and the
              file-icon fallback can be styled independently. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <FileCardThumbnail
          src={thumbnailUrl}
          blurHash={file.blurHash}
          mimeType={file.mimeType}
          alt={displayName}
        />
      </div>
      {/* Overlay checkbox — its center sits on the thumbnail's top-right corner
          (top 20 / right 24 padding minus half the 17px box), the same fixed
          spot whether or not a thumbnail rendered. Fades/scales in on hover or
          when selected (and always on touch, which has no hover), and is skipped
          by Tab. */}
      {checkboxPlacement === "overlay" && (
        <div
          className="absolute"
          style={{
            top: 11.5,
            right: 15.5,
            opacity: showCheckbox ? 1 : 0,
            transform: showCheckbox ? "scale(1)" : "scale(0.8)",
            // `visibility` also takes a hidden checkbox out of the
            // accessibility tree (and hit-testing), as the list row does.
            visibility: showCheckbox ? "visible" : "hidden",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.30))",
            transition:
              "opacity 0.18s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), visibility 0.18s",
          }}
        >
          <SelectionCheckbox
            checked={isSelected}
            onChange={() => onToggleSelect(file.uid)}
            itemName={displayName}
            size={17}
            // The card is the tab stop wherever Space toggles the selection.
            // Where it opens the preview instead ("preview" activation, i.e. no
            // hover), the checkbox is the only keyboard route to selecting.
            tabIndex={activation === "select" ? -1 : undefined}
          />
        </div>
      )}
      {/* Name bar — same 12% horizontal padding as thumbnail */}
      <div className="flex shrink-0 items-center gap-2 px-[12%] py-2">
        <div className="min-w-0 flex-1 overflow-hidden">
          {/* Tooltip is scoped to the name text only — it shows when the
                cursor is over the file name, not anywhere on the card.
                Suppressed during marquee drag-selection. */}
          <Tooltip
            title={displayName}
            enterDelay={400}
            placement="top"
            disableHoverListener={selecting}
          >
            <div className="truncate text-[15px] font-normal leading-[22px]">
              {displayName}
            </div>
          </Tooltip>
          <div className="text-[12px] leading-[22px] text-[#747474]">
            {formatFileSize(file.size, i18n.language)}
          </div>
        </div>
        {checkboxPlacement === "nameBar" && (
          <SelectionCheckbox
            checked={isSelected}
            onChange={() => onToggleSelect(file.uid)}
            itemName={displayName}
          />
        )}
        {onMoreActions && (
          <Tooltip title={t("More actions")} placement="top">
            <IconButton
              size="small"
              aria-label={t("More actions")}
              onClick={(e) => {
                e.stopPropagation();
                onMoreActions(e.currentTarget, file);
              }}
              onDoubleClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              // Stop the pointer event too, so the card's press (scale) feedback
              // doesn't fire when the kebab is pressed.
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                width: 28,
                height: 28,
                mr: -0.5,
                color: "#1A1A1A",
                flex: "none",
              }}
            >
              <FontAwesomeIcon
                icon={faEllipsisVertical as IconProp}
                style={{ fontSize: 16 }}
              />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  );
});

/**
 * Full-bleed thumbnail used inside {@link FileCard}.
 *
 * Two states, keyed off the BlurHash (its presence is the signal that the
 * backend produced a thumbnail, see {@link fileMayHaveThumbnail}):
 *  - no BlurHash: a centred, scaled-up file-type icon,
 *  - BlurHash: the decoded BlurHash canvas, with the thumbnail image fading in
 *    on top once it loads.
 */
function FileCardThumbnail({
  src,
  blurHash,
  mimeType,
  alt,
}: {
  src?: string;
  blurHash?: string;
  mimeType?: string;
  alt: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);

  // No BlurHash means the backend produced no thumbnail: show the file icon.
  if (!blurHash) {
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        style={{ padding: "40px 40px 0 40px" }}
      >
        <FileIcon
          mimeType={mimeType}
          fileName={alt}
          className="h-full w-full"
          flat
        />
      </div>
    );
  }

  // BlurHash present: the framed square holds the placeholder, with the
  // thumbnail fading in over it once it loads. A missing thumbnail just never
  // fades in, leaving the BlurHash in place (no explicit error handling needed).
  return (
    <div className="h-full w-full" style={{ padding: "20px 24px 0 24px" }}>
      {/* Inset square with a 0.5px white inner stroke (fileThumbnailSpec).
          Proportional padding mirrors Flutter's _kTileToThumbnailPadding
          (21/174 ≈ 12%). */}
      <div
        className="relative aspect-square w-full overflow-hidden"
        style={{ border: "0.5px solid #FFFFFF" }}
      >
        <BlurHashCanvas
          hash={blurHash}
          className="absolute inset-0 h-full w-full"
        />
        {src && (
          <img
            src={src}
            alt={alt}
            // Native lazy load: the browser defers the fetch until the card
            // nears the viewport and paces requests itself. Decode async to
            // keep it off the main thread.
            loading="lazy"
            decoding="async"
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300"
            // Fades in over the BlurHash once decoded.
            style={{ opacity: isLoaded ? 1 : 0 }}
            onLoad={() => setIsLoaded(true)}
          />
        )}
      </div>
    </div>
  );
}
